import { useEffect, useState } from 'react';
import type { EstadoTrabajo, TrabajoProgramado } from '@laser/core';
import { useActualizarTrabajo, useCatalogo } from '../../api/hooks';
import type { CambiosTrabajo, TrabajoDetalle } from '../../api/tipos';
import { Campo, Confirmar, Insignia, mensajeError, useAvisar } from '../../componentes/ui';
import { ESTADOS, ETIQUETA_ESTADO, SIGUIENTES } from '../../lib/estados';
import { aFechaHoraInput, desdeFechaInput, fechaHora, hora } from '../../lib/fechas';
import { holguraTexto } from '../../lib/trabajo';

export function Gestion({ trabajo, programado }: { trabajo: TrabajoDetalle; programado?: TrabajoProgramado }) {
  const cat = useCatalogo();
  const actualizar = useActualizarTrabajo(trabajo.id);
  const avisar = useAvisar();
  const [compromiso, setCompromiso] = useState(aFechaHoraInput(trabajo.fechaCompromiso));
  const [cancelar, setCancelar] = useState(false);
  useEffect(() => { setCompromiso(aFechaHoraInput(trabajo.fechaCompromiso)); }, [trabajo.fechaCompromiso]);

  const guardar = (c: CambiosTrabajo, ok?: string) => actualizar.mutate(c, {
    onSuccess: () => ok && avisar(ok, 'ok'),
    onError: (e) => avisar(mensajeError(e), 'bad'),
  });
  const cambiarEstado = (e: EstadoTrabajo) => {
    if (e === trabajo.estado) return;
    if (e === 'cancelado') { setCancelar(true); return; }
    guardar({ estado: e }, `Estado: ${ETIQUETA_ESTADO[e]}`);
  };
  const compromisoIso = desdeFechaInput(compromiso);
  const compromisoCambiado = compromisoIso != null && Date.parse(compromisoIso) !== Date.parse(trabajo.fechaCompromiso);
  const maqProgramada = programado ? cat.maquina.get(programado.maquinaId)?.nombre : null;

  return (
    <section className="tarjeta" aria-labelledby="gest-t">
      <h2 id="gest-t">Estado y programa</h2>
      <div className="acciones-estado">
        {SIGUIENTES[trabajo.estado].map((s, i) => (
          <button key={s.a} type="button" className={`btn ${i === 0 ? (s.a === 'cortando' ? 'btn--naranja' : 'btn--primario') : ''}`}
            disabled={actualizar.isPending} onClick={() => cambiarEstado(s.a)}>{s.texto}</button>
        ))}
        {trabajo.estado === 'en_revision' && <span className="meta">Para cotizar: “Aprobar y mandar link de pago”.</span>}
      </div>

      <div className="campos" style={{ marginTop: 12 }}>
        <Campo etiqueta="Cambiar estado a" htmlFor="g-estado">
          <select id="g-estado" value={trabajo.estado} onChange={(e) => cambiarEstado(e.target.value as EstadoTrabajo)} disabled={actualizar.isPending}>
            {ESTADOS.map((e) => <option key={e} value={e}>{ETIQUETA_ESTADO[e]}</option>)}
          </select>
        </Campo>
        <Campo etiqueta="Máquina asignada" htmlFor="g-maquina">
          <select id="g-maquina" value={trabajo.maquinaAsignadaId ?? ''} disabled={actualizar.isPending}
            onChange={(e) => guardar({ maquinaAsignadaId: e.target.value || null }, 'Máquina actualizada')}>
            <option value="">Sin asignar (decide el programa)</option>
            {cat.maquinas.map((m) => <option key={m.id} value={m.id}>{m.nombre}{m.activa ? '' : ' (inactiva)'}</option>)}
          </select>
        </Campo>
        <Campo etiqueta="Fecha compromiso" htmlFor="g-compromiso">
          <input id="g-compromiso" type="datetime-local" value={compromiso} onChange={(e) => setCompromiso(e.target.value)} />
        </Campo>
        <Campo etiqueta="Prioridad manual" ayuda="Mayor = se corta antes. 0 = normal.">
          <div className="fila" style={{ gap: 6, flexWrap: 'nowrap' }}>
            <button type="button" className="btn btn--sm" aria-label="Bajar prioridad" disabled={actualizar.isPending || trabajo.prioridadManual <= 0}
              onClick={() => guardar({ prioridadManual: Math.max(0, trabajo.prioridadManual - 1) })}>−</button>
            <b className="num" style={{ minWidth: 24, textAlign: 'center' }} aria-live="polite">{trabajo.prioridadManual}</b>
            <button type="button" className="btn btn--sm" aria-label="Subir prioridad" disabled={actualizar.isPending}
              onClick={() => guardar({ prioridadManual: trabajo.prioridadManual + 1 })}>+</button>
          </div>
        </Campo>
      </div>
      {compromisoCambiado && (
        <div className="fila" style={{ justifyContent: 'flex-end', marginTop: 8 }}>
          <button type="button" className="btn btn--sm" onClick={() => setCompromiso(aFechaHoraInput(trabajo.fechaCompromiso))}>Deshacer</button>
          <button type="button" className="btn btn--sm btn--primario" disabled={actualizar.isPending}
            onClick={() => guardar({ fechaCompromiso: compromisoIso! }, 'Fecha compromiso guardada')}>Guardar fecha</button>
        </div>
      )}

      <div className="linea" style={{ marginTop: 10 }}>
        <span className="muted">Programa</span>
        {programado ? (
          <span className="derecha celda-2">
            <b>{fechaHora(programado.inicio)}–{hora(programado.fin)}</b>
            <small>{maqProgramada} · <Insignia tono={programado.tarde ? 'bad' : 'good'}>{holguraTexto(programado.holguraMin)}</Insignia></small>
          </span>
        ) : <span className="meta">sin programar</span>}
      </div>

      <Confirmar abierto={cancelar} titulo="¿Cancelar el trabajo?" tono="peligro" textoConfirmar="Cancelar trabajo" ocupado={actualizar.isPending}
        onCancelar={() => setCancelar(false)}
        onConfirmar={() => actualizar.mutate({ estado: 'cancelado' }, { onSuccess: () => { setCancelar(false); avisar('Trabajo cancelado'); }, onError: (e) => avisar(mensajeError(e), 'bad') })}>
        <p style={{ marginTop: 6 }}>{trabajo.folio} de {trabajo.cliente.nombre} sale de la cola y del programa.{trabajo.pago?.estado === 'pagado' ? ' Ya está pagado: el reembolso se hace aparte.' : ''}</p>
      </Confirmar>
    </section>
  );
}

export function Notas({ trabajo }: { trabajo: TrabajoDetalle }) {
  const actualizar = useActualizarTrabajo(trabajo.id);
  const avisar = useAvisar();
  const [texto, setTexto] = useState(trabajo.notasInternas);
  useEffect(() => { setTexto(trabajo.notasInternas); }, [trabajo.notasInternas]);
  const cambiado = texto !== trabajo.notasInternas;
  return (
    <section className="tarjeta" aria-labelledby="notas-t">
      <h2 id="notas-t">Notas internas</h2>
      <div className="campo">
        <textarea aria-label="Notas internas (no las ve el cliente)" value={texto} maxLength={4000} onChange={(e) => setTexto(e.target.value)}
          placeholder="Qué se revisó, ajustes de potencia, a quién se le avisó…" />
      </div>
      <div className="fila fila--entre" style={{ marginTop: 8 }}>
        <span className="meta">No las ve el cliente.</span>
        <button type="button" className="btn btn--sm btn--primario" disabled={!cambiado || actualizar.isPending}
          onClick={() => actualizar.mutate({ notasInternas: texto }, { onSuccess: () => avisar('Notas guardadas', 'ok'), onError: (e) => avisar(mensajeError(e), 'bad') })}>
          Guardar notas
        </button>
      </div>
    </section>
  );
}
